import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateThemeMutation,
  useGetThemeQuery,
  useUpdateThemeMutation
} from '../../../../../services/endpoints/content/theme';
import { getCurrentUser } from '../../../../../services/helper';
import { LessonPlanThemesForm, Theme, ThemeLessonPlansSaveForm } from '../../../../../types/theme.type';
import { FadeIn } from '../../../../animations/FadeIn';
import { ThemeImagesForm } from './ThemeImagesForm';
import { ThemeForm } from './ThemesForm';
import { canEditThemes } from '../../../../../utils/rbac';
import {normalizeThemeLessonPlans} from "../../../../../utils/lessonPlanUtils";

export interface FormValues {
  id?: number;
  name: string;
  is_public?: string;
  is_ase?: string;
  details: string;
  lesson_plans: LessonPlanThemesForm[];
  images: (File | null)[];
  image_metadata: { id?: number; is_featured: boolean; destroy: boolean }[];
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;
export type CreateFormValues = Overwrite<
  FormValues,
  {
    lesson_plans?: ThemeLessonPlansSaveForm[];
  }
>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
  theme?: Theme;
}

const themeLinksMap = (id?: number | string) => {
  return {
    edit: `/admin/content/themes/${id}/edit`,
    detail: `/admin/content/themes/${id}`,
    list: `/admin/content/themes`
  };
};

interface AddEditThemeProps {
  editing?: boolean;
}

export const AddEditTheme: React.FC<AddEditThemeProps> = ({ editing }) => {
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createTheme] = useCreateThemeMutation();
  const [updateTheme] = useUpdateThemeMutation();

  const {
    data: themeData,
    error: themeError,
    isLoading: themeLoading
  } = useGetThemeQuery(Number(params.id!), { skip: !editing || !params.id });

  const currentUser = getCurrentUser();

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    is_public: 'true',
    is_ase: 'false',
    details: '',
    lesson_plans: [],
    images: [],
    image_metadata: []
  });

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    is_public: Yup.string().required('Publish Status is required'),
    is_ase: Yup.string(),
    details: Yup.string().required('Description is required')
  });

  useEffect(() => {
    if (editing && themeError && !themeLoading) {
      addToast('Error while loading admin', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, themeError, themeLoading]);

  useEffect(() => {
    let values;
    if (themeData && themeData?.theme) {
      values = themeData.theme;
    }

    if (values?.name)
      setInitialValues({
        id: values.id,
        name: values.name,
        is_public: values.is_public ?? 'false',
        is_ase: (values as any).is_ase ? 'true' : 'false',
        details: values.details,
        lesson_plans: normalizeThemeLessonPlans(values).map((lp) => {          let ret: LessonPlanThemesForm = {
            id: { value: lp.id, label: lp.name },
            featured_on: lp.theme_lesson_plans.featured_on,
            name: lp.name,
            purpose: lp.theme_lesson_plans.purpose
          };
          return ret;
        }),
        // Keep images slots aligned with image_metadata so new uploads land at the correct index.
        images: values.theme_images.map(() => null),
        image_metadata: values.theme_images.map((img) => ({
          id: img.id,
          is_featured: img.is_featured,
          destroy: false
        }))
      });

    if (!canEditThemes(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/content/themes`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeData]);

  const formatFormValue = (formValue?: FormValues): CreateFormValues => {
    return JSON.parse(JSON.stringify(formValue)) as CreateFormValues;
  };

  const handleAddTheme = async (formValue: FormValues) => {
    try {
      let submission = formatFormValue(formValue);
      //TODO: lessonPlan is working, but it's not saving,
      // check how to fix: {"error":["\"lessonPlans\" is not allowed"]}

      // submission.lesson_plans = formValue.lesson_plans.map(r => {
      //   let ret: LessonPlanThemesForm = {
      //     id: r.id,
      //     name: r.name,
      //     featured_on:r.featured_on
      //   }
      //   return ret;
      // })

      const lessonPlans = formValue.lesson_plans.map((r) => ({
        lesson_plan_id: r.id.value,
        featured_on: r.featured_on,
        purpose: r.purpose
      }));

      if (lessonPlans) delete submission.lesson_plans;

      if (editing) {
        const res = await updateTheme({
          ...submission,
          theme_lesson_plans: lessonPlans,
          id: Number(params.id)!,
          images: formValue.images,
          image_metadata: formValue.image_metadata
        }).unwrap();
        if (res && res.theme) {
          navigate(themeLinksMap(res.theme.id!)?.list);
          addToast(`Lessons Plan updated succesfully`, 'success');
        }
      } else {
        const res = await createTheme({
          ...submission,
          theme_lesson_plans: lessonPlans,
          images: formValue.images,
          image_metadata: formValue.image_metadata
        }).unwrap();
        if (res && res.theme) {
          navigate(themeLinksMap(res.theme.id!)?.list);
          addToast('Lessons Plan created succesfully', 'success');
        } else throw new Error('A problem happened while creating Lessons Plan');
      }
    } catch (e) {
      if (e.data && e.data.error) {
        addToast(e.data.error[0], 'error');
      }
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text="Back"
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Theme
          </h2>
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleAddTheme}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              touched,
              errors,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <ThemeForm
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                />
                <ThemeImagesForm
                  errors={errors}
                  touched={touched}
                  values={values}
                  setFieldValue={setFieldValue}
                  theme={themeData?.theme}
                />

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={submitForm}
                      className="w-100"
                      variant="primary"
                    >
                      {editing ? 'Update Theme' : 'Create Theme'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
